/**
 * The pipeline board (F2).
 *
 * Drag-and-drop with three accessible paths to the same outcome, because PRD
 * §9.7 requires drag not to be the only one:
 *
 *   1. Pointer drag.
 *   2. Keyboard drag — dnd-kit's own sensor, announced through a live region.
 *   3. A "Move to stage" select on every card, which is the path people find.
 *
 * Below 1024px the nine columns become a stage selector and a single column
 * (plan §7.3). Nine columns at 375px are unusable at any scroll speed.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 6.1, 6.3, 6.7, PRD §9.7, §7.3
 */
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { StartupSummary } from '@nexus/contracts';

import type { Stage } from '../../../components/domain/stages';
import { STAGES } from '../../../components/domain/stages';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { Select } from '../../../components/ui/Select';
import { useMediaQuery } from '../../../lib/use-media-query';
import { groupByStage } from '../board-grouping';

import {
  boardCollisionDetection,
  boardKeyboardCoordinates,
  buildAnnouncements,
  buildDragHandlers,
  overlayStartup,
  stageSelectHandler,
  type TranslateFn,
} from './board-drag';
import { BoardCard } from './BoardCard';
import { BoardColumn } from './BoardColumn';
import { StageDropTargets } from './StageDropTargets';

/** A move awaiting acknowledgement of unmet exit criteria (PRD §5.3). */
export interface PendingMove {
  startupId: string;
  startupName: string;
  stage: Stage;
  warnings: string[];
}

export interface StartupBoardProps {
  startups: readonly StartupSummary[];
  /** Resolves with any warnings; rejects on a genuine failure. */
  onMove: (startupId: string, stage: Stage, acknowledge: boolean) => Promise<string[]>;
  onOpen: (id: string) => void;
  canEdit: boolean;
}

export function StartupBoard({
  startups,
  onMove,
  onOpen,
  canEdit,
}: StartupBoardProps): React.JSX.Element {
  const { t } = useTranslation();
  const groups = useMemo(() => groupByStage(startups), [startups]);

  /* Which column the narrow layout shows. Defaults to the first stage rather
     than to "all", because a single column of everything is not a board. */
  const [visibleStage, setVisibleStage] = useState<Stage>(STAGES[0]);
  const [pending, setPending] = useState<PendingMove | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  /* 1024px, matching the `lg` breakpoint the rest of the page uses. */
  const isWide = useMediaQuery('(min-width: 64rem)');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      /* A small distance before a drag starts, so a tap on the card's name
         opens it instead of beginning a drag nobody intended. */
      activationConstraint: { distance: 6 },
    }),
    /* Our own coordinate getter, not `sortableKeyboardCoordinates`: dnd-kit's
       default navigates only within the dragged item's own `SortableContext`,
       and each column is a separate one — so with the default a card can be
       picked up by keyboard and then never leave its column. That makes the
       accessible path PRD §9.7 requires non-functional for the one operation
       the board exists to perform. */
    useSensor(KeyboardSensor, {
      coordinateGetter: boardKeyboardCoordinates(startups, STAGES),
      /* Scroll the board so the target column is on screen, rather than
         refusing to move to it. The wide board scrolls horizontally, and
         dnd-kit's default keyboard behaviour will not translate a card outside
         the scroll viewport — which silently makes the last few stages
         unreachable by keyboard on a narrow window. */
      scrollBehavior: 'auto',
    }),
  );

  /** Looks a startup up by id. */
  const findStartup = (id: string): StartupSummary | undefined =>
    startups.find((startup) => startup.id === id);

  /* Cast to the narrow signature the builders declare: i18next's `t` satisfies
     it at runtime, but its overload set is not assignable to a single
     signature. */
  const announcements = buildAnnouncements(startups, t as TranslateFn);

  /** Requests a move, holding it back if the API reports unmet criteria. */
  const requestMove = async (
    startupId: string,
    stage: Stage,
    acknowledge: boolean,
  ): Promise<void> => {
    const startup = findStartup(startupId);
    /* Every caller passes an id from the rendered list, so this cannot fire; it
       exists so a future caller cannot crash the board with a stale id. */
    /* v8 ignore next */
    if (!startup) return;

    setMoveError(null);

    try {
      const warnings = await onMove(startupId, stage, acknowledge);

      /* PRD §5.3: unmet criteria WARN, they do not block. The dialog asks; it
         does not refuse. */
      if (warnings.length > 0) {
        setPending({ startupId, startupName: startup.name, stage, warnings });
        return;
      }

      setPending(null);
    } catch {
      setMoveError(t('board.moveFailed', { name: startup.name }));
      setPending(null);
    }
  };

  /* v8 ignore start -- these closures are invoked only by dnd-kit on a real
     drag, which needs element geometry jsdom does not compute. What they DO is
     `requestMove`, `setMoveError` and `setActiveId`; the first two are covered
     through the exit-criteria dialog path, and `buildDragHandlers` — the wiring
     that decides whether a drop becomes a move — is tested directly. */
  const dragHandlers = buildDragHandlers(
    startups,
    (startupId, stage, acknowledge) => {
      void requestMove(startupId, stage, acknowledge);
    },
    () => {
      setMoveError(null);
    },
    setActiveId,
  );
  /* v8 ignore stop */

  /* The card under the cursor. dnd-kit leaves the original in place (faded) and
     the overlay is what actually follows the pointer — without one, a dragged
     card stays in its column and only the cursor moves, which reads as the
     board ignoring the drag. */
  const activeStartup = overlayStartup(startups, activeId);

  const visibleGroups = groups.filter((group) => group.stage === visibleStage);

  return (
    <>
      {/* Stage selector, narrow screens only (plan §7.3). Conditional rather
          than `lg:hidden`, so on the wide board its Select is not in the tree
          at all — hidden, it is still a focusable control claiming to choose a
          stage that the wide board does not have. */}
      {!isWide && (
        <div className="mb-3">
          <label className="block">
            <span className="sr-only">{t('board.stageSelector')}</span>
            <Select
              value={visibleStage}
              onValueChange={stageSelectHandler(setVisibleStage)}
              options={groups.map((group) => ({
                value: group.stage,
                label: `${t(`stage.${group.stage}`)} (${String(group.startups.length)})`,
              }))}
              ariaLabel={t('board.stageSelector')}
            />
          </label>
        </div>
      )}

      {/* Instructions, available to everyone rather than hidden: a sighted
          keyboard user needs them too. */}
      <p className="sr-only" id="board-instructions">
        {t('board.dragInstructions')}
      </p>

      {moveError === null ? null : (
        <p role="alert" className="text-sem-bad mb-3 text-sm">
          {moveError}
        </p>
      )}

      <DndContext
        sensors={sensors}
        /* `closestCorners` for the pointer, but a keyboard drag names its
           target exactly — `boardKeyboardCoordinates` already decided which
           stage the arrow key meant, and re-deriving it from geometry can land
           on a different chip in the narrow layout's wrapped grid. */
        collisionDetection={boardCollisionDetection}
        accessibility={{
          announcements,
          screenReaderInstructions: { draggable: t('board.dragInstructions') },
        }}
        onDragStart={dragHandlers.onDragStart}
        onDragEnd={dragHandlers.onDragEnd}
        onDragCancel={dragHandlers.onDragCancel}
      >
        {/* Exactly ONE layout is rendered, chosen in JavaScript rather than by
            `lg:hidden`. Rendering both and hiding one with CSS — which is how
            this was built — registers every stage as a droppable twice, and the
            hidden copy's rectangle is 0x0 at the origin. dnd-kit then resolves
            a stage id to whichever it registered first, so a keyboard drag aims
            at a zero-sized rect and lands on whatever is nearest the origin.
            The breakpoint has to reach JS because it changes what EXISTS, not
            just what is visible. */}
        {isWide ? (
          /* Nine columns, scrolling horizontally. `items-stretch` makes every
             column the height of the tallest, and the floor keeps a sparse
             board presenting full-height targets rather than nine 90px stubs. */
          /* No horizontal scroll at usual widths: the nine columns share the
             board's width, each with a 180px floor. The board scrolled before,
             and dnd-kit clamps a keyboard drag's translation to the scroll
             viewport — so the far stages were reachable by pointer and simply
             unreachable by keyboard, a worse failure than narrow columns.
             Below the floor the row scrolls again, and a keyboard user has the
             narrow layout's stage list instead. */
          <div
            aria-label={t('board.label')}
            className="gap-s2 flex min-h-[60vh] items-stretch overflow-x-auto pb-2"
          >
            {groups.map((group) => (
              <BoardColumn
                key={group.stage}
                stage={group.stage}
                startups={group.startups}
                onOpen={onOpen}
                canEdit={canEdit}
              />
            ))}
          </div>
        ) : (
          <div
            aria-label={t('board.label')}
            className="gap-s3 flex min-h-[50vh] flex-col"
          >
            {visibleGroups.map((group) => (
              <BoardColumn
                key={group.stage}
                stage={group.stage}
                startups={group.startups}
                onOpen={onOpen}
                canEdit={canEdit}
                isSingleColumn
              />
            ))}

            {/* The other stages, as drop targets. With one column on screen
                there is nothing else to drop onto, so without these a touch
                user could not move a card at all. */}
            {canEdit && <StageDropTargets current={visibleStage} stages={STAGES} />}
          </div>
        )}
        {/* Rendered in a portal above everything, so a card dragged out of a
            scrolling column is not clipped by it.

            `DragOverlay` renders its children only while dnd-kit's own context
            reports an active drag, and that state comes from element geometry
            jsdom does not compute — so this JSX is unreachable in a unit test
            whatever seam is used. The decision it depends on is `overlayStartup`,
            which is tested directly; what remains here is the markup, verified
            in a browser. */}
        <DragOverlay>
          {
            /* v8 ignore next -- `DragOverlay` renders its children only while
               dnd-kit reports an active drag, which needs geometry jsdom does
               not compute, so `activeStartup` is always null here. The decision
               itself is `overlayStartup`, tested directly. */
            activeStartup && (
              <ul className="w-[250px] cursor-grabbing">
                <BoardCard startup={activeStartup} onOpen={onOpen} canEdit={false} />
              </ul>
            )
          }
        </DragOverlay>
      </DndContext>

      {/* Unmet exit criteria (task 6.7): shown, then the move proceeds if the
          user says so. PRD §5.3 is explicit that this warns rather than
          blocks — the team knows things the database does not. */}
      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          /* v8 ignore next -- Radix calls this with `true` only when the
             dialog opens itself, which it never does here: `open` is driven
             entirely by `pending`. */
          if (!open) setPending(null);
        }}
        title={t('board.warningsTitle')}
        description={
          pending === null
            ? undefined
            : t('board.warningsIntro', {
                name: pending.startupName,
                stage: t(`stage.${pending.stage}`),
              })
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setPending(null);
              }}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              onClick={() => {
                /* v8 ignore next -- the button only exists while `pending` is
                   set, so the guard is a type-level necessity rather than a
                   reachable branch. */
                if (pending !== null) {
                  void requestMove(pending.startupId, pending.stage, true);
                }
              }}
            >
              {t('board.warningsProceed')}
            </Button>
          </>
        }
      >
        <ul className="text-body flex list-disc flex-col gap-1 pl-5 text-sm">
          {(pending?.warnings ?? []).map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      </Dialog>
    </>
  );
}
